/* =====================================================================
 * LiveAgentCardRail — 라이브 에이전트 turn 단위 도구 결과 카드 레일
 *
 * 음성 모델은 짧고 자연스러운 한 마디만 발화하고, 정확한 제목/저자/URL
 * 같은 메타데이터는 이 카드 레일에 시각적으로 표시한다.
 *
 * 데이터 흐름
 * -----------
 *
 *   서버 (live_agent.py / web_search_agent.py)
 *     → ``cards.update``  (WS)
 *   useLiveAgentSession.handleWsMessage
 *     → liveAgentStore.appendCards
 *        - 마지막 assistant 메시지가 있으면 그 메시지의 ``cards`` 에 merge
 *        - 없으면 ``pendingTurnCards`` 에 보관 → 다음 ``addMessage`` 가
 *          새 assistant 를 만들 때 그 메시지의 ``cards`` 로 흡수
 *   LiveAgentPage 의 메시지 mapping 안에서 ``<LiveAgentCardRail cards={msg.cards} />``
 *     를 ``AssistantCard`` (= 답변 버블 + 액션 버튼) *바로 아래* 에 mount.
 *     버블 안에 들어가지 않으므로 카드가 늘어나도 답변 버블 자체는 절대
 *     가로로 자라지 않는다.
 *   LiveAgentCardRail (이 파일)
 *     → 채팅 max-width 안에서 가로 스크롤 + ‹ › 버튼 + 풍부한 카드 비주얼
 *
 * 디자인 원칙
 * -----------
 *
 *  - **테마 brand 컬러 사용** : `theme.colors.brand` 를 써서 askyourai
 *    (주황) / samsung enterprise (파랑) 같은 tenant 별 포인트 컬러가
 *    자동으로 반영되게 한다.  카드의 호버/포커스 ring 은 brand 컬러를 따른다.
 *  - **버블 max-width 안** : 카드 레일은 ``Row`` 의 max-width
 *    (= ``theme.sizes.maxMessageWidth``) 안에 100% 폭으로 배치되어
 *    채팅 영역을 절대 넘지 않는다. 가로 overflow 는 내부 스크롤로 처리.
 *  - **좌우 스크롤 ‹ ›** : 카드 폭 + gap 만큼 한 번에 점프하는 버튼.
 *    스크롤 가능 여부에 따라 enable/disable 된다 (resize 와 휠 모두 반영).
 *  - **단순 카드** : favicon + 도메인(헤더) → 제목 2줄.  Globe 류 leading
 *    kind 아이콘은 favicon 과 시각적으로 중복이라 의도적으로 제거했다.
 *    google_search ``segment.text`` 기반 snippet 도 카드끼리 자주 겹치고
 *    title 과 의미가 겹쳐 기본적으로 숨긴다 (백엔드에서 ``snippet=None``).
 *    og:image 가 들어오는 paper/article 카드에선 좌측에 64x64 thumbnail.
 * ===================================================================== */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  ExternalLink,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import type { LiveAgentCard } from '@auw/stores';
import { MarkdownRenderer } from '@auw/workspace-ui';

/* ------------------------------------------------------------------ */
/* Animations                                                          */
/* ------------------------------------------------------------------ */

const railFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const cardFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const CARD_WIDTH = 280;
const CARD_GAP = 10;
/** ‹ › 버튼 클릭 시 한 번에 점프할 픽셀. 카드 ~1.5장 정도. */
const SCROLL_STEP_PX = (CARD_WIDTH + CARD_GAP) * 1.5;

/* ------------------------------------------------------------------ */
/* Styled                                                              */
/* ------------------------------------------------------------------ */

/**
 * 외부 컨테이너 — 메시지 버블 하단의 폭 100% 영역을 차지한다.
 * ``Row`` 의 max-width 가 채팅창 max-width 와 동일하므로 자연스럽게
 * 채팅창을 넘지 않는다. position: relative 는 ‹ › 버튼의 absolute
 * 기준이 된다.
 */
const RailOuter = styled.div`
  position: relative;
  width: 100%;
  margin-top: 10px;
  animation: ${railFadeIn} 0.25s ease-out;

  @media (max-width: ${({ theme }) => theme.breakpoints?.mobile ?? '768px'}) {
    margin-top: 8px;
  }
`;

const RailScroller = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${CARD_GAP}px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  /* 좌우 끝에서 카드 일부가 잘려 보이지 않도록 inline 살짝 padding. */
  padding: 2px 2px 4px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const NavButton = styled.button<{ $side: 'left' | 'right'; $visible: boolean }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: -2px;' : 'right: -2px;')}
  transform: translateY(-50%);
  z-index: 2;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(28,28,32,0.85)' : 'rgba(255,255,255,0.92)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  display: ${({ $visible }) => ($visible ? 'inline-flex' : 'none')};
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.10);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.12s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    color: ${({ theme }) => theme.colors.brand};
  }
  &:active {
    transform: translateY(-50%) scale(0.94);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints?.mobile ?? '768px'}) {
    /* 모바일은 터치 스와이프로 충분 — 시각적 노이즈 줄이려고 숨긴다. */
    display: none;
  }
`;

/* kind 별 leading 아이콘은 의도적으로 제거됐다.  원래 web 카드에는 lucide
 * ``Globe`` 가 항상 좌측에 들어갔지만 favicon 이 같은 정보를 더 풍부하게
 * 표현해 시각적 중복이었다.  thumbnail (og:image) 이 있을 때만 좌측에
 * 표시하고, 그 외에는 헤더(favicon + 도메인) → 제목 만 노출한다. */

const Card = styled.a<{ $clickable: boolean }>`
  flex: 0 0 auto;
  width: ${CARD_WIDTH}px;
  min-height: 88px;
  padding: 10px 12px 11px;
  border-radius: 14px;
  display: flex;
  gap: 10px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  animation: ${cardFadeIn} 0.22s ease-out;
  transition:
    transform 0.15s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease;

  ${({ theme }) => css`
    background: ${theme.mode === 'dark'
      ? 'rgba(28,28,32,0.62)'
      : 'rgba(255,255,255,0.78)'};
    border: 1px solid ${theme.mode === 'dark'
      ? 'rgba(255,255,255,0.10)'
      : 'rgba(0,0,0,0.06)'};
    box-shadow: ${theme.mode === 'dark'
      ? '0 6px 16px rgba(0,0,0,0.28)'
      : '0 6px 16px rgba(0,0,0,0.06)'};
  `}

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      &:hover {
        transform: translateY(-1px);
        border-color: ${theme.colors.brand};
        box-shadow: 0 10px 22px rgba(0, 0, 0, 0.10);
      }
      &:active {
        transform: translateY(0);
      }
      &:focus-visible {
        outline: 2px solid ${theme.colors.brand};
        outline-offset: 2px;
      }
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints?.mobile ?? '768px'}) {
    width: 240px;
    min-height: 80px;
  }
`;

const Thumbnail = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
`;

const FaviconImg = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  object-fit: contain;
  flex-shrink: 0;
  background: transparent;
`;

const CardBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colors.brand};
  text-transform: uppercase;
`;

const CardSourceLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.02em;
`;

const CardExternal = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.35)'};
`;

const CardTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const CardSnippet = styled.div`
  font-size: 11.5px;
  line-height: 1.45;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.50)'};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

/**
 * google_search grounding 의 ``web.title`` 또는 ``segment.text`` 폴백은
 * Gemini 가 마크다운 emphasis (``**bold**``, ``__bold__``, ``*italic*``,
 * ``_italic_``, `` ` ``) 를 그대로 박아 보내는 경우가 많다. 카드 UI 는
 * 일반 텍스트로만 렌더하므로 raw asterisk 가 그대로 노출되는 것을 막기
 * 위해 클라이언트단에서 한 번 더 잘라낸다.  백엔드 ``grounding_extractor``
 * 가 이미 strip 하지만 LLM 응답이 진화해 새 emphasis 패턴이 생기면 여기서
 * 추가로 막아준다.
 */
function stripMarkdownEmphasis(input: string | null | undefined): string {
  if (!input) return '';
  let s = input;
  // **bold**, __bold__, *italic*, _italic_ — 둘러싸는 토큰만 제거.
  s = s.replace(/\*\*(.+?)\*\*/g, '$1');
  s = s.replace(/__(.+?)__/g, '$1');
  s = s.replace(/(^|[^\w*])\*(?!\s)([^*\n]+?)\*(?!\w)/g, '$1$2');
  s = s.replace(/(^|[^\w_])_(?!\s)([^_\n]+?)_(?!\w)/g, '$1$2');
  // inline code backticks
  s = s.replace(/`([^`]+?)`/g, '$1');
  return s.trim();
}

/**
 * 외부 링크가 안전하게 새 탭으로 열리도록 ``target="_blank" rel="..."`` 을
 * 강제하고, 같은 탭으로 보내려는 ``javascript:`` 등의 prefix 는 차단한다.
 */
function isSafeExternalUrl(url: string | null | undefined): url is string {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  return /^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed);
}

/**
 * 사람이 읽기 좋은 source label 폴백 우선순위.
 *
 * 백엔드에서 ``source_label`` 이 명확히 채워지면 그것을 그대로 쓰고,
 * "Web" 같은 너무 generic 한 라벨이거나 비어 있을 때만 ``subtitle``
 * (도메인) → ``title`` 의 도메인 부분 → 'WEB' 로 폴백한다.
 */
function resolveSourceLabel(c: LiveAgentCard): string {
  const trimmed = (c.source_label ?? '').trim();
  if (trimmed && trimmed.toLowerCase() !== 'web') return trimmed;
  const sub = (c.subtitle ?? '').trim();
  if (sub) return sub;
  const t = (c.title ?? '').trim();
  if (/^(?:[a-z0-9-]+\.)+[a-z]{2,}$/i.test(t)) return t;
  return 'WEB';
}

/* ------------------------------------------------------------------ */
/* Content card (kind === 'content')                                    */
/* ------------------------------------------------------------------ */
/*
 * ``show_on_screen`` 툴이 push 하는 본문 카드. 코드 예제·표·정형 텍스트처럼
 * 음성으로 읽기보다 보여줄 산출물이라, 링크형 가로 레일(280px)과 달리
 * 메시지 폭 전체를 쓰는 stacked 블록으로 렌더한다. 본문은 메인 채팅과
 * 동일한 ``MarkdownRenderer`` 로 그려 코드 하이라이트/복사 버튼/표를 그대로
 * 재사용한다(중복 구현 금지).
 */

const ContentBlock = styled.div`
  width: 100%;
  margin-top: 10px;
  padding: 12px 14px 6px;
  border-radius: 14px;
  animation: ${cardFadeIn} 0.22s ease-out;
  overflow: hidden;

  ${({ theme }) => css`
    background: ${theme.mode === 'dark'
      ? 'rgba(28,28,32,0.62)'
      : 'rgba(255,255,255,0.78)'};
    border: 1px solid ${theme.mode === 'dark'
      ? 'rgba(255,255,255,0.10)'
      : 'rgba(0,0,0,0.06)'};
    box-shadow: ${theme.mode === 'dark'
      ? '0 6px 16px rgba(0,0,0,0.28)'
      : '0 6px 16px rgba(0,0,0,0.06)'};
  `}
`;

const ContentTitle = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colors.brand};
  margin: 2px 2px 8px;
  word-break: break-word;
`;

const PlainBody = styled.div`
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 0 2px 8px;
`;

/**
 * 코드 본문을 마크다운 펜스로 감쌀 때, 본문 안에 들어 있는 백틱 런보다 긴
 * 펜스를 골라 fence 깨짐을 막는다(코드에 ``` 가 들어와도 안전).
 */
function safeFence(body: string): string {
  const runs = body.match(/`+/g) || [];
  const longest = runs.reduce((m, r) => Math.max(m, r.length), 0);
  return '`'.repeat(Math.max(3, longest + 1));
}

const ContentCard: React.FC<{ card: LiveAgentCard }> = ({ card }) => {
  const fmt = (card.body_format ?? 'plain').toLowerCase();
  const body = card.body ?? '';
  let rendered: React.ReactNode;
  if (!body.trim()) {
    rendered = null;
  } else if (fmt === 'plain') {
    rendered = <PlainBody>{body}</PlainBody>;
  } else if (fmt === 'code') {
    const fence = safeFence(body);
    const lang = (card.language ?? '').toLowerCase();
    rendered = (
      <MarkdownRenderer content={`${fence}${lang}\n${body}\n${fence}`} />
    );
  } else {
    // markdown (및 향후 미지원 포맷의 안전한 기본값)
    rendered = <MarkdownRenderer content={body} />;
  }
  return (
    <ContentBlock role="group" aria-label={card.title || 'Generated content'}>
      {card.title ? <ContentTitle>{card.title}</ContentTitle> : null}
      {rendered}
    </ContentBlock>
  );
};

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export interface LiveAgentCardRailProps {
  /**
   * 이 메시지 turn 동안 도구가 push 한 카드들. 비어 있거나 undefined 이면
   * 컴포넌트가 ``null`` 을 반환해 빈 공간을 차지하지 않는다.
   */
  cards?: LiveAgentCard[] | null;
}

export const LiveAgentCardRail: React.FC<LiveAgentCardRailProps> = ({
  cards,
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /** 스크롤 가능 여부를 즉시 측정 — content/viewport 변화에 모두 대응. */
  const recomputeScrollability = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1);
  }, []);

  /** mount/카드변경 직후 즉시 측정. */
  useLayoutEffect(() => {
    recomputeScrollability();
  }, [cards?.length, recomputeScrollability]);

  /** scroll/resize 양쪽 모두 watch — 윈도우 크기 변화 + 사이드바 토글 등. */
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => recomputeScrollability();
    el.addEventListener('scroll', onScroll, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      const obs = new ResizeObserver(recomputeScrollability);
      obs.observe(el);
      return () => {
        el.removeEventListener('scroll', onScroll);
        obs.disconnect();
      };
    }
    return () => {
      el.removeEventListener('scroll', onScroll);
    };
  }, [recomputeScrollability]);

  const scrollBy = useCallback((delta: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const handleCardClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, url: string | null | undefined) => {
      if (!isSafeExternalUrl(url)) {
        e.preventDefault();
      }
    },
    [],
  );

  if (!cards || cards.length === 0) return null;

  // 본문 카드(``content``)는 전체폭 stacked 블록, 링크 카드는 가로 레일.
  const contentCards = cards.filter((c) => c.kind === 'content');
  const linkCards = cards.filter((c) => c.kind !== 'content');

  return (
    <>
      {contentCards.map((c) => (
        <ContentCard key={c.id} card={c} />
      ))}
      {linkCards.length > 0 && (
    <RailOuter>
      <NavButton
        type="button"
        $side="left"
        $visible={canScrollLeft}
        aria-label="Scroll cards left"
        onClick={() => scrollBy(-SCROLL_STEP_PX)}
      >
        <ChevronLeft size={16} />
      </NavButton>
      <NavButton
        type="button"
        $side="right"
        $visible={canScrollRight}
        aria-label="Scroll cards right"
        onClick={() => scrollBy(SCROLL_STEP_PX)}
      >
        <ChevronRight size={16} />
      </NavButton>
      <RailScroller
        ref={scrollerRef}
        role="list"
        aria-label="Reference cards for this answer"
      >
        {linkCards.map((c) => {
          const clickable = isSafeExternalUrl(c.url);
          const href = clickable ? c.url ?? undefined : undefined;
          const sourceLabel = resolveSourceLabel(c);
          const cleanedTitle = stripMarkdownEmphasis(c.title);
          const cleanedSnippet = stripMarkdownEmphasis(c.snippet);
          const titleLooksLikeDomain =
            /^(?:[a-z0-9-]+\.)+[a-z]{2,}$/i.test(cleanedTitle);
          /* title 자체가 도메인이고 source label 이 같은 도메인이면 이중
             표시를 피한다.  이런 경우 title 칸에는 snippet 의 첫 줄을
             내려 정보 밀도를 높인다. */
          const visibleTitle =
            titleLooksLikeDomain && sourceLabel === cleanedTitle
              ? (cleanedSnippet || cleanedTitle)
              : cleanedTitle;
          /* snippet 은 web 카드에서 ``segment.text`` 기반이라 다른 카드와
             자주 겹친다.  visibleTitle 과 동일하거나 그 prefix 인 경우
             감춘다. */
          const showSnippet =
            !!cleanedSnippet &&
            cleanedSnippet !== visibleTitle &&
            !visibleTitle.startsWith(cleanedSnippet) &&
            !cleanedSnippet.startsWith(visibleTitle);
          return (
            <Card
              key={c.id}
              $clickable={clickable}
              href={href}
              target={clickable ? '_blank' : undefined}
              rel={clickable ? 'noopener noreferrer' : undefined}
              onClick={(e) => handleCardClick(e, c.url)}
              role="listitem"
              aria-label={`${sourceLabel}: ${visibleTitle}`}
            >
              {c.thumbnail_url ? (
                <Thumbnail
                  src={c.thumbnail_url}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : null}
              <CardBody>
                <CardHeader>
                  {c.favicon_url ? (
                    <FaviconImg
                      src={c.favicon_url}
                      alt=""
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : null}
                  <CardSourceLabel>{sourceLabel}</CardSourceLabel>
                  {clickable && (
                    <CardExternal aria-hidden>
                      <ExternalLink size={12} />
                    </CardExternal>
                  )}
                </CardHeader>
                <CardTitle>{visibleTitle}</CardTitle>
                {showSnippet ? (
                  <CardSnippet>{cleanedSnippet}</CardSnippet>
                ) : null}
              </CardBody>
            </Card>
          );
        })}
      </RailScroller>
    </RailOuter>
      )}
    </>
  );
};

export default LiveAgentCardRail;
